/**
 * TemplatesPanel — the Editor's Templates tab: the authoring list, plus the routing into
 * the creation Wizard and the mask Editor.
 *
 * Clicking a template opens it in the EDIT/REWORK form (TemplateEditor) — NOT straight into
 * batch. The ONLY paths into the batch are the explicit "Use this template for batch" action
 * (on the row and inside the editor), which adds it to the selection store.
 *
 * WS-B B-6: the LIST itself moved to `TemplateListPage`, shared with `CustomerTemplates`.
 * What stays here is the only thing that was ever genuinely this page's own — the
 * wizard/editor routing. The list's fetching, caching, searching, filtering, tree,
 * per-device state and row actions are all one implementation now (D-5).
 *
 * `pageTitle` is forwarded to the list (W4.10): the Editor tab passes `false` because the
 * route's own `<h1>` is already the page title, so the list's heading renders as an `<h2>`.
 *
 * WS-Z1 (Rule B): the authoring LIST is `AdminTemplatesSurface` — its own composition, the
 * dense index with every status, version and approval, and ONE commit ("New template") in the
 * action bar. It is not the customer page with extra buttons; the two share the engine
 * (`useTemplateSurface`) and nothing else.
 *
 * Demo / offline note: `listTemplates()` may throw (no cloud, demo mode). `useTemplateList`
 * degrades to the cached rows, else the empty state + Generate CTA, and never crashes the
 * shell.
 */
import { useCallback, useState } from "react";
import { useWizardStore } from "../stores/wizard.js";
import { Wizard } from "../wizard/Wizard.js";
import { TemplateEditor } from "./TemplateEditor.js";
import { AdminTemplatesSurface } from "./TemplateListPage.js";
import { notifyCatalogChanged } from "./catalogInvalidation.js";
import type { ProductTemplate } from "@genz/platform";
import "./customer-templates.css";

export function TemplatesPanel({ pageTitle = true }: { pageTitle?: boolean } = {}) {
  // #1 — the wizard's OPEN flag lives in the store so an in-progress template survives
  // navigating away and back (and the discard guard can close it).
  // A2: only a wizard THIS page opened — `#generate` opens its own.
  const generating = useWizardStore((s) => s.open && (s.origin ?? "editor") === "editor");
  const [editing, setEditing] = useState<ProductTemplate | null>(null);

  const startGenerate = useCallback(() => {
    // Fresh flow every time: reset the wizard, then mark it OPEN (store-backed).
    useWizardStore.getState().reset();
    useWizardStore.getState().setOrigin("editor");
    useWizardStore.getState().setOpen(true);
  }, []);

  /** Leaving the wizard or the editor re-pulls the list: a re-edit may have opened a fresh
   *  draft, and a new template must appear. `notifyCatalogChanged` is the signal
   *  `useTemplateList` already listens on, so the refresh goes through the ONE data contract
   *  instead of this file keeping a second loader alive just for two call sites. */
  const finishGenerate = useCallback(() => {
    useWizardStore.getState().setOpen(false);
    notifyCatalogChanged("template");
  }, []);

  const closeEditor = useCallback(() => {
    setEditing(null);
    notifyCatalogChanged("template");
  }, []);

  if (generating) {
    return <Wizard onDone={finishGenerate} onClose={finishGenerate} />;
  }

  if (editing) {
    return <TemplateEditor template={editing} onClose={closeEditor} />;
  }

  return (
    <AdminTemplatesSurface
      onOpenEditor={setEditing}
      onNewTemplate={startGenerate}
      pageTitle={pageTitle}
    />
  );
}
